import React, {
  useState,
  useEffect
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './HomePage.css'
import AuthModal from './components/AuthModal';
import WaitlistModal from './components/WaitlistModal';
import Logo from './components/Logo';
import Starfield from './components/Starfield';
import { supabase } from './supabase';


import { FaMicrophoneLines } from "react-icons/fa6";
import { FaRegCalendarAlt, FaUsers, FaChartLine, FaRegClock } from "react-icons/fa";

import PricingTable from './billing/PricingTable';
import { useBilling } from './billing/BillingContext';
import { savePlanIntent } from './billing/format';
import { isSignupRefused } from './auth/noAccess';

// Kept outside the component so the array identity is stable; Starfield
// restarts its animation loop whenever this prop changes.
const STAR_COLOR = [255, 255, 255];

// What changes for the customer, not a list of tools
const OUTCOMES = [
  {
    icon: <FaRegCalendarAlt />,
    title: 'Never start the week with a blank page',
    text: 'Every Monday there\'s a plan: what to post, where, and when, with a target for each post.',
  },
  {
    icon: <FaUsers />,
    title: 'Every post speaks to a real buyer',
    text: 'We build your customer profiles from your website and reviews, and write each post for one of them.',
  },
  {
    icon: <FaMicrophoneLines />,
    title: 'Sounds like you, not like AI',
    text: 'Your brand voice is learned once and used everywhere: hooks, captions, articles, ads and video scripts.',
  },
  {
    icon: <FaChartLine />,
    title: 'Know which posts bring sign-ups',
    text: 'Tracked links show what gets clicks. Each week the plan keeps what worked and rewrites what didn\'t.',
  },
  {
    icon: <FaRegClock />,
    title: 'Get your evenings back',
    text: 'A week of posts in the time it takes to drink a coffee, so you can get back to building.',
  },
];

const HomePage = ({ user: appUser }) => {
  const { pricing } = useBilling();
  const [interval, setBillingInterval] = useState('year');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [loadedCards, setLoadedCards] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState('sign_in');
  // New visitors join the waitlist; { source, plan?, interval?, noAccess? } while it's open.
  // It opens on arrival when Google sign in was refused for having no account
  // (sent here by /auth/callback, or by Supabase itself with the error in the URL).
  const [waitlist, setWaitlist] = useState(() => (
    new URLSearchParams(window.location.search).has('noAccess') || isSignupRefused()
      ? { source: 'google', noAccess: true }
      : null
  ));
  // After deleting an account the app lands here with ?accountDeleted=1
  const [accountDeleted, setAccountDeleted] = useState(() => new URLSearchParams(window.location.search).has('accountDeleted'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Signed-in visitors can still view the landing page; the header button takes them to the
    // dashboard. Fresh sign ins are sent there by AuthModal (email) or /auth/callback (Google).
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const currentUser = user ?? appUser; // appUser covers dev auto sign-in
      setIsAuthenticated(Boolean(currentUser));
    };

    checkUser();

    // Check if we should show the auth modal based on URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const showAuth = urlParams.get('showAuth');
    if (showAuth === 'true') {
      setShowAuthModal(true);
    }
    // Arriving from the reset-password email: let them choose a new password
    if (urlParams.get('resetPassword') === 'true') {
      setAuthView('update_password');
      setShowAuthModal(true);
    }

    // Keep the header button in sync with sign in / sign out
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setAuthView('update_password');
        setShowAuthModal(true);
      } else if (event === 'SIGNED_IN') {
        setIsAuthenticated(true);
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [appUser]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Clear the refused sign in from the address bar, so a reload doesn't show it again
  useEffect(() => {
    if (waitlist?.noAccess) window.history.replaceState(null, '', '/');
  }, [waitlist?.noAccess]);

  // While on the landing page: hide the page scrollbar (scrolling still
  // works) and darken html/body so overscroll doesn't flash white.
  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => document.documentElement.classList.remove('landing-page');
  }, []);

  useEffect(() => {
    const loadCards = () => {
      const cards = document.querySelectorAll('.feature-card');
      cards.forEach((card, index) => {
        setTimeout(() => {
          setLoadedCards(prev => [...prev, index]);
        }, index * 200);
      });
    };

    loadCards();
  }, []);

  const devRedirect = 'http://localhost:3000/auth/callback'
  const prodRedirect = 'https://light-speed-ai.com/auth/callback'
  const redirectTo = process.env.NODE_ENV === 'development' ? devRedirect : prodRedirect
  // Sign in stays open for people who already have an account
  const handleAuthButtonClick = () => {
    setIsMenuOpen(false);
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setWaitlist(null);
      setAuthView('sign_in');
      setShowAuthModal(true);
    }
  };

  const openWaitlist = (source, plan, planInterval) => {
    setIsMenuOpen(false);
    setShowAuthModal(false);
    setWaitlist({ source, plan, interval: planInterval });
  };

  const handleCtaClick = () => (isAuthenticated ? navigate('/dashboard') : openWaitlist('hero'));

  const handleAuthSuccess = (user) => {
    setIsAuthenticated(true);
    setShowAuthModal(false);
    navigate('/dashboard');
  };

  // Visitors join the waitlist, noting the plan they picked; signed-in users go
  // to the app, where a paid plan is picked up on the billing page
  const handlePlanSelect = (plan, planInterval) => {
    if (!isAuthenticated) {
      openWaitlist('pricing', plan, planInterval);
      return;
    }
    if (plan !== 'free') savePlanIntent(plan, planInterval);
    navigate(plan === 'free' ? '/dashboard' : '/dashboard/billing');
  };

  const scrollTo = (selector) => {
    setIsMenuOpen(false);
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });
  };

  // const handleParallax = (e) => {
  //   const gradientText = document.querySelector('.gradient-text');
  //   if (gradientText) {
  //     const rect = gradientText.getBoundingClientRect();
  //     const centerX = rect.left + rect.width / 2;
  //     const centerY = rect.top + rect.height / 2;
  //     const moveX = (e.clientX - centerX) * 0.02;
  //     const moveY = (e.clientY - centerY) * 0.02;
  //     gradientText.style.transform = `translate(${moveX}px, ${moveY}px)`;
  //   }
  // };

  return (
    <div className="container landing">
      <Starfield
        className="starfield"
        starCount={1500}
        starColor={STAR_COLOR}
        speedFactor={0.03}
      />
      <div className="glass-header">
        <div className="header-content">
          <button type="button" className="logo-container" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="light speed, back to top">
            <Logo size={36} textSize={20} />
          </button>

          <div className="header-controls">
            {/* <button
              className="auth-button"
              onClick={() => handleAuthButtonClick('sign_in')}
            >
              {isAuthenticated ? 'Sign Out' : 'Sign In'}
              {userName ? userName : ''}
            </button> */}

            <label className="burger" htmlFor="burger">
              <input
                type="checkbox"
                id="burger"
                checked={isMenuOpen}
                onChange={(e) => setIsMenuOpen(e.target.checked)}
              />
              <span></span>
              <span></span>
              <span></span>
            </label>
          </div>

          {isMenuOpen && (
            <div className="dropdown-menu" role="menu">
              <button type="button" role="menuitem" className="menu-item" onClick={() => scrollTo('.features-section')}>
                How it works
              </button>
              <button type="button" role="menuitem" className="menu-item" onClick={() => scrollTo('#pricing')}>
                Pricing
              </button>
              <div className="menu-divider" />
              {!isAuthenticated && (
                <button type="button" role="menuitem" className="menu-item" onClick={() => openWaitlist('menu')}>
                  Join the waitlist
                </button>
              )}
              <button type="button" role="menuitem" className="menu-item" onClick={handleAuthButtonClick}>
                {isAuthenticated ? 'Go to dashboard' : 'Sign in'}
              </button>
            </div>
          )}
        </div>
      </div>

      {accountDeleted && (
        <div className="landing-toast" role="status">
          Your account and its data have been deleted.
          <button type="button" aria-label="Dismiss" onClick={() => { setAccountDeleted(false); window.history.replaceState(null, '', '/'); }}>×</button>
        </div>
      )}

      {showAuthModal && (
        <AuthModal
          key={authView}
          initialView={authView}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          redirectTo={redirectTo}
          onJoinWaitlist={() => openWaitlist('sign_in')}
        />
      )}

      {waitlist && (
        <WaitlistModal
          {...waitlist}
          onClose={() => setWaitlist(null)}
          onSignIn={handleAuthButtonClick}
        />
      )}

      <div className="hero-container">
        <div
          className="hero-wrapper"
        // onMouseMove={handleParallax}
        >
          <div className={`hero-text-content ${isVisible ? 'fade-in' : ''}`}>
            <h1 className="hero-title">
              Turn social into sign-ups
              <span className="gradient-text">at Light Speed</span>
            </h1>
            <p className="hero-description">
              Point us at your website. Every week we plan and write posts for the customers you want, in your
              voice, and show you which ones bring sign-ups. You just hit post.
            </p>
            <button className="cta-button pulse-animation" onClick={handleCtaClick}>
              {isAuthenticated ? 'Go to dashboard' : 'Join the waitlist'}
            </button>
            <p className="hero-fine">We’re letting people in a few at a time. Free plan, no card needed.</p>
          </div>
        </div>

        <div className="features-section">
          <h3 className="features-title">What changes when you use light speed</h3>
          <div className="features-cards">
            {OUTCOMES.map((outcome, index) => (
              <div key={outcome.title} className={`feature-card hover-lift ${loadedCards.includes(index) ? 'fade-in-up' : ''}`}>
                <div className="feature-icon">
                  {outcome.icon}
                </div>
                <h4>{outcome.title}</h4>
                <p>{outcome.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="pricing-section" id="pricing">
          <h3 className="features-title">Pricing that pays for itself</h3>
          <p className="pricing-lead">
            One new customer a month covers Growth. Start free, upgrade when you're posting every week.
          </p>
          <PricingTable
            pricing={pricing}
            interval={interval}
            onIntervalChange={setBillingInterval}
            onSelect={handlePlanSelect}
            mode={isAuthenticated ? 'landing' : 'waitlist'}
            dark
          />
          <p className="pricing-fine">
            Prices in US dollars; at checkout you can pay in your local currency.
            Pay by card, Apple Pay or Google Pay. Cancel anytime from the app.
            Plans renew automatically; see the <Link to="/terms#plans">Terms</Link>.
          </p>
        </div>
      </div>

      <footer className="footer">
        {/* <div className="footer-container">
          <div className="footer-section">
            <h4>Company</h4>
            <p>About Us</p>
            <p>Contact</p>
            <p>Privacy Policy</p>
          </div>
          <div className="footer-section">
            <h4>Resources</h4>
            <p>Documentation</p>
            <p>API</p>
            <p>Blog</p>
          </div>
          <div className="footer-section">
            <h4>Follow Us</h4>
            <div className="social-icons">
              <a href="#" className="social-icon">𝕏</a>
              <a href="#" className="social-icon">in</a>
              <a href="#" className="social-icon">yt</a>
            </div>
          </div>
        </div> */}
        <nav className="footer-links" aria-label="Legal">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          {/* <a href="mailto:hello@light-speed-ai.com">Contact</a> */}
        </nav>
        <p className="footer-credits">© 2026 Tantalum Technology Corp. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default HomePage;